import AIAgentCard from "../sidebar-widgets/AIAgentCard";
import React from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import { VisibilityOutlined as VisibilityOutlinedIcon } from "../icons/FigmaControlIcons";
import { SequenceActionIcon } from "../ActionButtonIcons";
import { formatLocalDateTime } from "../../utils/dateTime";
import {
  PILL_BUTTON_BG,
  pillButtonInteractiveSx,
  quickActionPillButtonSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";
import { SIDEBAR_WIDGET_TITLE_SX } from "../sidebar-widgets/sidebarWidgetStyles";

const PURPLE = PILL_BUTTON_BG;
const BORDER = "#E5E7EB";
const TEXT = "#344054";
const MUTED = "#98A2B3";

const cardSx = {
  backgroundColor: "#fff",
  border: `1px solid ${BORDER}`,
  borderRadius: 2,
  p: 2,
};

const quickActionButtonSx = {
  ...quickActionPillButtonSx,
  height: 48,
  px: 2.8,
  fontSize: 14,
};

function WidgetTitle({ children }) {
  return (
    <Typography
      className="heading"
      sx={{
        ...SIDEBAR_WIDGET_TITLE_SX,
        mb: 1.6,
      }}
    >
      {children}
    </Typography>
  );
}

export function DirectMessagesQuickActionsCard({
  onCall,
  onEmail,
  onSchedule,
  onCreateSequence,
}) {
  return (
    <Box sx={cardSx}>
      <WidgetTitle>Quick Actions</WidgetTitle>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button
          className={withPillButtonClassName()}
          startIcon={<CallOutlinedIcon sx={{ fontSize: 15 }} />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onCall}
        >
          Call
        </Button>
        <Button
          className={withPillButtonClassName()}
          startIcon={<MailOutlineRoundedIcon sx={{ fontSize: 15 }} />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onEmail}
        >
          Email
        </Button>
        <Button
          className={withPillButtonClassName()}
          startIcon={<ScheduleOutlinedIcon sx={{ fontSize: 15 }} />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onSchedule}
        >
          Schedule
        </Button>
        <Button
          className={withPillButtonClassName()}
          startIcon={<SequenceActionIcon size={15} />}
          sx={[pillButtonInteractiveSx, quickActionButtonSx]}
          onClick={onCreateSequence}
        >
          Create Sequence
        </Button>
      </Stack>
    </Box>
  );
}

export function DirectMessagesAIAgentCard({ thread }) {
  return <AIAgentCard entityType="message thread" entityId={thread?.id} entityName={thread?.name || "Selected conversation"}
    promptText="Review this conversation and help plan the next step."
    noteText="Ask for a summary, draft a response, or attach supporting files."
    linkText="" contextText={JSON.stringify({ name: thread?.name, subject: thread?.subject, preview: thread?.preview_text })} />;
}

export function DirectMessagesEmailsCard({
  emails = [],
  onPreview,
  onCompose,
}) {
  return (
    <Box sx={cardSx}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <WidgetTitle>Emails</WidgetTitle>
        <DraftsOutlinedIcon sx={{ fontSize: 16, color: PURPLE, mt: -1 }} />
      </Box>

      <Stack spacing={1.35}>
        {emails.map((email, index) => (
          <Box key={email.id || index}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.65 }}>
                  <DraftsOutlinedIcon sx={{ fontSize: 13, color: MUTED }} />
                  <Typography sx={{ fontSize: 10.75, color: MUTED }}>
                    {email.sent_at
                      ? formatLocalDateTime(email.sent_at, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : ""}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    mt: 0.7,
                    fontSize: 11.75,
                    color: TEXT,
                    lineHeight: 1.55,
                  }}
                >
                  {email.subject || email.preview_text || "-"}
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.1}>
                <IconButton size="small" onClick={() => onPreview?.(email)}>
                  <VisibilityOutlinedIcon sx={{ fontSize: 16, color: PURPLE }} />
                </IconButton>
                <IconButton size="small" onClick={() => onCompose?.(email)}>
                  <SendRoundedIcon sx={{ fontSize: 16, color: PURPLE }} />
                </IconButton>
                <IconButton size="small">
                  <MoreVertRoundedIcon sx={{ fontSize: 16, color: PURPLE }} />
                </IconButton>
              </Stack>
            </Box>

            <Box sx={{ textAlign: "right", mt: 0.35 }}>
              <Button
                onClick={() => onPreview?.(email)}
                sx={{
                  minWidth: "auto",
                  px: 0,
                  textTransform: "none",
                  fontSize: 11.25,
                  fontWeight: 700,
                  color: PURPLE,
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                View
              </Button>
            </Box>

            {index < emails.length - 1 ? <Divider sx={{ mt: 1.1, borderColor: BORDER }} /> : null}
          </Box>
        ))}
      </Stack>

      <Box sx={{ textAlign: "right", mt: 1.25 }}>
        <Button
          sx={{
            minWidth: "auto",
            px: 0,
            textTransform: "none",
            fontSize: 11.5,
            fontWeight: 700,
            color: PURPLE,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          View all Emails
        </Button>
      </Box>
    </Box>
  );
}
